"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.shopifyWebPublisher = exports.SHOPIFY_WEB_PUBLISHER_BG = exports.SHOPIFY_WEB_PUBLISHER_COLOR = exports.SHOPIFY_WEB_PUBLISHER_CATEGORY = exports.shopifyWebPublisherArgsSchema = exports.ShopifyWebPublisherAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var webPublisher_1 = require("./webPublisher");
// Shopify-specific extended actions (12 actions)
var shopifyExtendedActionSchema = zod_1.z.enum([
    'getArticle',
    'createArticle',
    'updateArticle',
    'getPage',
    'createPage',
    'updatePage',
    'getProduct',
    'createProduct',
    'updateProduct',
    'listProducts',
    'listPages',
    'listBlogPosts',
]);
// Enhanced Shopify argument schemas for extended actions
var shopifyGetArticleArgsSchema = zod_1.z.object({
    id: zod_1.z.string().describe('The Shopify article ID or handle to fetch'),
    blogHandle: zod_1.z
        .string()
        .nullish()
        .describe('Optional blog handle if using article handle instead of ID'),
});
var shopifyCreateArticleArgsSchema = zod_1.z.object({
    title: zod_1.z.string().describe('The article title'),
    bodyHtml: zod_1.z.string().describe('The article content in HTML format'),
    blogHandle: zod_1.z.string().default('news').describe('The blog handle to publish to'),
    handle: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
    summary: zod_1.z.string().nullish().describe('Optional article excerpt or summary'),
    tags: zod_1.z.array(zod_1.z.string()).nullish().describe('Array of tags to assign to the article'),
    author: zod_1.z.string().default('Shop Admin').describe('Article author name'),
    published: zod_1.z.boolean().default(true).describe('Whether to publish immediately'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyUpdateArticleArgsSchema = zod_1.z.object({
    articleId: zod_1.z.string().describe('The Shopify article ID to update'),
    blogHandle: zod_1.z.string().describe('The blog handle containing the article'),
    title: zod_1.z.string().nullish().describe('Optional new title'),
    bodyHtml: zod_1.z.string().nullish().describe('Optional new content in HTML format'),
    handle: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
    summary: zod_1.z.string().nullish().describe('Optional new excerpt or summary'),
    tags: zod_1.z.array(zod_1.z.string()).nullish().describe('Array of tags to assign (replaces existing)'),
    author: zod_1.z.string().nullish().describe('Optional new author name'),
    published: zod_1.z.boolean().nullish().describe('Optional publish status'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyGetPageArgsSchema = zod_1.z.object({
    id: zod_1.z.string().describe('The Shopify page ID or handle to fetch'),
});
var shopifyCreatePageArgsSchema = zod_1.z.object({
    title: zod_1.z.string().describe('The page title'),
    bodyHtml: zod_1.z.string().describe('The page content in HTML format'),
    handle: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
    published: zod_1.z.boolean().default(true).describe('Whether to publish immediately'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyUpdatePageArgsSchema = zod_1.z.object({
    pageId: zod_1.z.string().describe('The Shopify page ID to update'),
    title: zod_1.z.string().nullish().describe('Optional new title'),
    bodyHtml: zod_1.z.string().nullish().describe('Optional new content in HTML format'),
    handle: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
    published: zod_1.z.boolean().nullish().describe('Optional publish status'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyGetProductArgsSchema = zod_1.z.object({
    id: zod_1.z.string().describe('The Shopify product ID or handle to fetch'),
});
var shopifyCreateProductArgsSchema = zod_1.z.object({
    title: zod_1.z.string().describe('The product title'),
    bodyHtml: zod_1.z.string().describe('The product description in HTML format'),
    handle: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
    productType: zod_1.z.string().nullish().describe('Optional product type'),
    vendor: zod_1.z.string().nullish().describe('Optional vendor name'),
    tags: zod_1.z.array(zod_1.z.string()).nullish().describe('Array of tags to assign to the product'),
    published: zod_1.z.boolean().default(true).describe('Whether to publish immediately'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyUpdateProductArgsSchema = zod_1.z.object({
    productId: zod_1.z.string().describe('The Shopify product ID to update'),
    title: zod_1.z.string().nullish().describe('Optional new title'),
    bodyHtml: zod_1.z.string().nullish().describe('Optional new description in HTML format'),
    handle: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
    productType: zod_1.z.string().nullish().describe('Optional new product type'),
    vendor: zod_1.z.string().nullish().describe('Optional new vendor name'),
    tags: zod_1.z.array(zod_1.z.string()).nullish().describe('Array of tags to assign (replaces existing)'),
    published: zod_1.z.boolean().nullish().describe('Optional publish status'),
    publishedAt: zod_1.z.string().nullish().describe('Optional publish date (ISO format)'),
});
var shopifyListProductsArgsSchema = zod_1.z.object({
    first: zod_1.z
        .number()
        .default(20)
        .describe('Number of products to retrieve (default: 20, max recommended: 50)'),
    after: zod_1.z.string().nullish().describe('Cursor for pagination - get products after this cursor'),
    query: zod_1.z
        .string()
        .nullish()
        .describe('Search query for products (e.g., "title:shirt", "vendor:nike")'),
    status: zod_1.z.enum(['ACTIVE', 'ARCHIVED', 'DRAFT']).nullish().describe('Filter products by status'),
});
var shopifyListPagesArgsSchema = zod_1.z.object({
    first: zod_1.z
        .number()
        .default(20)
        .describe('Number of pages to retrieve (default: 20, max recommended: 50)'),
    after: zod_1.z.string().nullish().describe('Cursor for pagination - get pages after this cursor'),
    published: zod_1.z
        .boolean()
        .nullish()
        .describe('Filter pages by published status (true for published, false for drafts)'),
});
var shopifyListBlogPostsArgsSchema = zod_1.z.object({
    blogHandle: zod_1.z
        .string()
        .nullish()
        .describe('Blog handle/name to list articles from. If not provided, will use "news" as default'),
    first: zod_1.z
        .number()
        .default(20)
        .describe('Number of articles to retrieve (default: 20, max recommended: 50)'),
    after: zod_1.z.string().nullish().describe('Cursor for pagination - get articles after this cursor'),
    published: zod_1.z
        .boolean()
        .nullish()
        .describe('Filter articles by published status (true for published, false for drafts)'),
});
// All Shopify Web Publisher actions (3 core + 12 extended = 15 total)
exports.ShopifyWebPublisherAction = zod_1.z.enum([
    // Core WebPublisher actions
    'create',
    'update',
    'subagentTaskRunner',
    // Extended Shopify actions
    'getArticle',
    'createArticle',
    'updateArticle',
    'getPage',
    'createPage',
    'updatePage',
    'getProduct',
    'createProduct',
    'updateProduct',
    'listProducts',
    'listPages',
    'listBlogPosts',
]);
// Shopify Web Publisher Args Schema
exports.shopifyWebPublisherArgsSchema = zod_1.z.object({
    action: exports.ShopifyWebPublisherAction.describe('CRITICAL TOOL ROUTING: Shopify web publishing action to perform. Core actions - create (ONLY with SEO Article Writer node references): publish new Shopify pages/blogs from seoArticleWriter output, update (ONLY with SEO Article Updater node references): modify existing Shopify content from seoArticleUpdater output, subagentTaskRunner: natural language Shopify tasks'),
    args: zod_1.z
        .union([
        // Core WebPublisher actions
        webPublisher_1.webPublisherArgsSchema.shape.args,
        // Extended Shopify actions
        shopifyGetArticleArgsSchema,
        shopifyCreateArticleArgsSchema,
        shopifyUpdateArticleArgsSchema,
        shopifyGetPageArgsSchema,
        shopifyCreatePageArgsSchema,
        shopifyUpdatePageArgsSchema,
        shopifyGetProductArgsSchema,
        shopifyCreateProductArgsSchema,
        shopifyUpdateProductArgsSchema,
        shopifyListProductsArgsSchema,
        shopifyListPagesArgsSchema,
        shopifyListBlogPostsArgsSchema,
    ])
        .describe('MANDATORY NODE TYPE RESTRICTIONS: Core actions require specific workflow node references - create actions MUST use seoArticleWriterNodeId, update actions MUST use seoArticleUpdaterNodeId. Extended actions use specific Shopify API parameters.'),
});
// All Shopify Web Publisher actions now use the unified WebPublisherResult format
var ShopifyWebPublisherAllResultsSchema = webPublisher_1.webPublisherResultSchema;
// Dynamic result schemas keyed by action - all use unified format now
var shopifyWebPublisherResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action'],
    variants: [
        // Core WebPublisher actions
        { when: { action: 'create' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'update' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'subagentTaskRunner' }, schema: webPublisher_1.webPublisherResultSchema },
        // Extended Shopify actions - all use unified format
        { when: { action: 'getArticle' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'createArticle' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'updateArticle' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'getPage' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'createPage' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'updatePage' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'getProduct' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'createProduct' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'updateProduct' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listProducts' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listPages' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listBlogPosts' }, schema: webPublisher_1.webPublisherResultSchema },
    ],
    defaultSchema: webPublisher_1.webPublisherResultSchema,
});
exports.SHOPIFY_WEB_PUBLISHER_CATEGORY = common_1.ToolCategorySchema.enum.integration;
exports.SHOPIFY_WEB_PUBLISHER_COLOR = common_1.BRAND_COLORS.SHOPIFY;
exports.SHOPIFY_WEB_PUBLISHER_BG = (0, common_1.createBgColor)(exports.SHOPIFY_WEB_PUBLISHER_COLOR);
// Shopify WebPublisher tool metadata
exports.shopifyWebPublisher = (0, common_1.createToolMetadata)({
    id: 'shopifyWebPublisher',
    displayName: 'Shopify Web Publisher',
    displayDescription: 'CRITICAL TOOL ROUTING: Shopify web publisher with strict node type restrictions. Core actions - create (ONLY with SEO Article Writer node references): publish new Shopify pages/blogs from seoArticleWriter output, update (ONLY with SEO Article Updater node references): modify existing Shopify content from seoArticleUpdater output, subagentTaskRunner: natural language Shopify tasks. Extended: full Shopify API operations for articles, pages, and products. Discovery: listProducts, listPages, listBlogPosts for content auditing',
    category: exports.SHOPIFY_WEB_PUBLISHER_CATEGORY,
    color: exports.SHOPIFY_WEB_PUBLISHER_COLOR,
    backgroundColor: exports.SHOPIFY_WEB_PUBLISHER_BG,
    icon: 'shopify',
    argsSchema: exports.shopifyWebPublisherArgsSchema,
}, shopifyWebPublisherResultSchemas);
